import { IAnnotation } from 'react-ace';
import type ReactAce from 'react-ace';
import {
  ChangeEvent,
  CSSProperties,
  LegacyRef,
  useEffect,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { FunctionRuntime, TestResponse } from '@rossum/api-client/hooks';
import { IconAlertCircle } from '@rossum/ui/icons/tabler';
import { Alert, Stack, SvgIcon } from '@rossum/ui/material';
import makeResizable from '../../../../../decorators/makeResizable';
import { MIN_FUNCTION_BOX_SECTION_HEIGHT } from '../../../../../decorators/makeResizable/config';
import { EventNameOptions, TabNames } from '../types';
import { ExtensionsInput } from './ExtensionsInput';
import { ExtensionsOutput } from './ExtensionsOutput';
import { InputBoxMenu } from './InputBoxMenu';
import { OutputBoxMenu } from './OutputBoxMenu';

type OwnProps = {
  assignedFunctionEventNames: Array<EventNameOptions>;
  canRun: boolean;
  canSave: boolean;
  currentEvent: EventNameOptions;
  currentFunctionOutput: TestResponse | undefined;
  currentObject: string;
  editorFooter: boolean;
  functionEvents: Array<EventNameOptions>;
  testFunctionError?: boolean;
  inputErrors: Array<IAnnotation>;
  inputRef: LegacyRef<ReactAce>;
  inputTmpValue: string;
  isInputValid: boolean;
  isRunning: boolean;
  onChange: (_value: string, _event?: ChangeEvent<Element>) => void;
  onLoad: () => void;
  onPrettify: (_value?: string) => void;
  onSave: () => void;
  runtime: FunctionRuntime;
  savedInput: string;
  setCurrentEvent: (_event: EventNameOptions) => void;
  setCurrentObject: (_objectRef: string) => void;
  setFooterResizing: (_resizing: boolean) => void;
  setInputErrors: (inputErrors: IAnnotation[]) => void;
  setIsInputValid: (_value: boolean) => void;
  setShowInputLoading: (loading: boolean) => void;
  showInputLoading: boolean;
  testFunction: () => void;
  isSaving?: boolean;
};

type InjectedProps = {
  resizing: boolean;
  style: CSSProperties;
  height: number;
};

type EditorFooterProps = OwnProps & InjectedProps;

const EditorFooterContent = ({
  assignedFunctionEventNames,
  canRun,
  canSave,
  currentEvent,
  currentFunctionOutput,
  currentObject,
  functionEvents,
  testFunctionError,
  height,
  inputErrors,
  inputRef,
  inputTmpValue,
  isInputValid,
  isRunning,
  onChange,
  onLoad,
  onPrettify,
  onSave,
  resizing,
  runtime,
  savedInput,
  setCurrentEvent,
  setCurrentObject,
  setFooterResizing,
  setInputErrors,
  setIsInputValid,
  setShowInputLoading,
  showInputLoading,
  style,
  testFunction,
  isSaving,
}: EditorFooterProps) => {
  const [showErrors, setShowErrors] = useState(false);
  const [activeResponseTab, setActiveResponseTab] =
    useState<TabNames>('response');

  useEffect(() => {
    setFooterResizing(resizing);
  }, [resizing, setFooterResizing]);

  return (
    // eslint-disable-next-line no-restricted-syntax -- old code
    <Stack style={style} direction="row">
      <ExtensionsInput
        onChange={onChange}
        value={inputTmpValue}
        forwardRef={inputRef}
        onValidate={annotations => {
          setInputErrors(
            annotations.filter(({ type }) => type === 'error') as IAnnotation[]
          );
          setIsInputValid(!annotations.some(({ type }) => type === 'error'));
        }}
        height={height}
        showInputLoading={showInputLoading}
      >
        <InputBoxMenu
          assignedFunctionEventNames={assignedFunctionEventNames}
          canRun={canRun}
          canSave={canSave}
          onSave={onSave}
          onLoad={onLoad}
          currentObject={currentObject}
          setCurrentObject={setCurrentObject}
          currentEvent={currentEvent}
          setCurrentEvent={setCurrentEvent}
          functionEvents={functionEvents}
          hasError={!isInputValid && !!inputErrors.length}
          isRunning={isRunning}
          onPrettify={onPrettify}
          onRun={testFunction}
          savedInput={savedInput}
          setShowInputLoading={setShowInputLoading}
          isSaving={isSaving}
        />
      </ExtensionsInput>
      <ExtensionsOutput
        value={currentFunctionOutput}
        activeTab={activeResponseTab}
        runtime={runtime}
      >
        <OutputBoxMenu
          activeTab={activeResponseTab}
          setActiveTab={setActiveResponseTab}
          hasError={testFunctionError}
          isRunning={isRunning}
        />
      </ExtensionsOutput>
      {inputErrors && !!inputErrors.length && (
        <Stack
          onMouseEnter={() => setShowErrors(true)}
          onMouseLeave={() => setShowErrors(false)}
          spacing={1}
          sx={{
            position: 'absolute',
            bottom: '10px',
            left: 'calc(25px + 25%)',
            transform: 'translateX(-50%)',
          }}
        >
          {[
            { row: 1, text: 'test' },
            { row: 2, text: 'test2' },
          ].map(({ row, text }) => (
            <Alert
              key={`${row}-${text}`}
              color="error"
              sx={{ display: showErrors ? 'flex' : 'none' }} // why is this hidden?
            >
              {`Error: ${text} at row ${row}`}
            </Alert>
          ))}
          <Alert
            icon={
              <SvgIcon fontSize="medium">
                <IconAlertCircle />
              </SvgIcon>
            }
            color="error"
          >
            <FormattedMessage
              id="components.editor.inputBox.errorMessage"
              values={{ count: inputErrors.length }}
            />
          </Alert>
        </Stack>
      )}
    </Stack>
  );
};

export const EditorFooter = (props: OwnProps) =>
  makeResizable(EditorFooterContent)({
    ...props,
    minHeight: MIN_FUNCTION_BOX_SECTION_HEIGHT,
  });
